/* SSi website – Mission page (manifesto edition).
 *
 * Warm + human, typographic + SVG-led, minimal photography.
 * Sections:
 *   MissionHero      – Roy quote + lede
 *   MissionZeina     – Cardiff Friday vignette (deep-red panel, gold pull)
 *   MissionWeb       – "Every language is a way of being human"
 *   MissionStats     – 7,000 / ~50% / 50–90% trio on red
 *   MissionWelshNot  – illustrated SVG tokens, same object across empires
 *   MissionSchools   – timeline + tally
 *   MissionGap       – most children don't get this
 *   MissionTrial     – bar chart of reverse bell curve + study link
 *   MissionDoing     – what we're trying to do (loop diagram)
 *   MissionContact   – short form + email
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

export function MissionHero() {
  return (
    <section style={mStyles.heroWrap} data-screen-label="Hero">
      <div style={mStyles.heroInner}>
        <div style={mStyles.heroKicker}>What drives us</div>
        <h1 style={mStyles.heroH1}>A better world is possible.</h1>
        <p style={mStyles.heroP}>As Arundhati Roy said so beautifully:</p>
        <figure style={mStyles.royFig}>
          <blockquote style={mStyles.royQuote}>On a quiet day I can hear her breathing.</blockquote>
        </figure>
        <p style={mStyles.heroP}>There's a lot we have to fix, of course. It's time for us to grow up and manage the planet sensibly, it's time for us to recognise that poverty is a human construct, and it's abundantly clear that we can't afford billionaires any more.</p>
        <p style={{...mStyles.heroP, ...mStyles.heroPFinal}}>And it's time for us to understand the central importance of language.</p>
        <p style={{...mStyles.heroP, ...mStyles.heroPFinal}}>I mean, <em>languages</em>.</p>
      </div>
    </section>
  );
}

function ZeinaLaughing() {
  // Cheerful laughing-girl head, three-quarter view. Soft warm skin tone, dark wavy hair,
  // open smile, eyes scrunched up in a laugh. Designed to read on the brand-red ground.
  return (
    <svg viewBox="0 0 320 320" width="100%" height="100%" role="img" aria-label="A girl laughing">
      <defs>
        <radialGradient id="zSkin" cx="50%" cy="45%" r="60%">
          <stop offset="0" stopColor="#f5cfa6"/>
          <stop offset="1" stopColor="#d8a073"/>
        </radialGradient>
        <linearGradient id="zHair" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#2a1308"/>
          <stop offset="1" stopColor="#4a2412"/>
        </linearGradient>
        <linearGradient id="zHairHi" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="rgba(255,255,255,.18)"/>
          <stop offset="1" stopColor="rgba(255,255,255,0)"/>
        </linearGradient>
      </defs>

      {/* soft halo */}
      <circle cx="160" cy="160" r="140" fill="rgba(255,255,255,.06)"/>

      {/* hair – back layer, big wavy shape */}
      <path d="M70 140 C 60 80, 110 38, 165 40 C 225 42, 262 80, 258 142 C 270 168, 264 208, 248 232 C 240 244, 224 250, 212 246 C 222 220, 224 196, 220 178 C 200 184, 178 184, 162 178 C 152 200, 134 214, 116 218 C 102 224, 88 220, 80 212 C 70 196, 66 170, 70 140 Z" fill="url(#zHair)"/>
      {/* hair highlights */}
      <path d="M96 110 C 110 80, 150 60, 190 64 C 220 66, 244 86, 250 118" stroke="url(#zHairHi)" strokeWidth="10" fill="none" strokeLinecap="round"/>

      {/* neck */}
      <path d="M140 230 L 140 268 Q 160 278 180 268 L 180 230 Z" fill="url(#zSkin)"/>
      <path d="M140 244 Q 160 252 180 244" stroke="rgba(0,0,0,.08)" strokeWidth="2" fill="none"/>

      {/* face */}
      <path d="M104 158 C 104 110, 132 84, 162 84 C 198 84, 222 112, 220 162 C 218 200, 198 232, 162 232 C 128 232, 106 202, 104 158 Z" fill="url(#zSkin)"/>

      {/* a few hair strands across the forehead */}
      <path d="M118 110 Q 140 96, 168 100" stroke="#2a1308" strokeWidth="6" fill="none" strokeLinecap="round"/>
      <path d="M170 96 Q 192 102, 206 118" stroke="#2a1308" strokeWidth="6" fill="none" strokeLinecap="round"/>

      {/* cheeks (laughing flush) */}
      <ellipse cx="126" cy="180" rx="14" ry="8" fill="#e88a7a" opacity=".55"/>
      <ellipse cx="200" cy="178" rx="14" ry="8" fill="#e88a7a" opacity=".55"/>

      {/* eyes – scrunched-up laughing arcs */}
      <path d="M120 158 Q 132 148, 144 158" stroke="#1a0c04" strokeWidth="4" fill="none" strokeLinecap="round"/>
      <path d="M178 158 Q 190 148, 202 158" stroke="#1a0c04" strokeWidth="4" fill="none" strokeLinecap="round"/>
      {/* tiny lash flicks */}
      <path d="M118 156 L 114 152" stroke="#1a0c04" strokeWidth="2" strokeLinecap="round"/>
      <path d="M204 156 L 208 152" stroke="#1a0c04" strokeWidth="2" strokeLinecap="round"/>

      {/* nose hint */}
      <path d="M160 170 Q 158 188, 164 194" stroke="rgba(0,0,0,.18)" strokeWidth="2" fill="none" strokeLinecap="round"/>

      {/* open laughing mouth */}
      <path d="M138 198 Q 162 230, 188 198 Q 174 218, 162 220 Q 150 218, 138 198 Z" fill="#5a1418"/>
      {/* upper teeth */}
      <path d="M144 200 Q 162 210, 182 200 L 182 206 Q 162 212, 144 206 Z" fill="#fff"/>
      {/* lip highlight */}
      <path d="M140 198 Q 162 192, 186 198" stroke="#c0606a" strokeWidth="3" fill="none" strokeLinecap="round"/>

      {/* small earring */}
      <circle cx="104" cy="196" r="3.5" fill="#f4c25e"/>
    </svg>
  );
}

export function MissionZeina() {
  return (
    <>
    <section style={mStyles.zeinaWrap} data-screen-label="Zeina vignette">
      <div className="m-zeina" style={{...mStyles.zeinaInner, ...mStyles.zeinaGrid}}>
        <div className="m-zeina__copy">
          <div style={mStyles.zeinaKicker}>It's Friday.</div>
          <p style={mStyles.zeinaLead}>Zeina is on her way home from school, walking through Grangetown in Cardiff, laughing.</p>
          <p style={mStyles.zeinaP}>She's chatting in Welsh to her friends, with bits of English and Arabic thrown in – it could easily become Arabic or English with bits of Welsh thrown in, they code-switch a lot, but they're too excited about the weekend to notice. They say thank you in Gujarati at the shop on the corner, and then suddenly they're singing in Korean. That's the big deal – there's a K-pop sensation playing at the castle on Saturday.</p>
        </div>
        <div className="m-zeina__art" style={mStyles.zeinaArtWrap} aria-hidden="true">
          <img src="/assets/mission/zeina-laughing-sketch.png" alt="" style={mStyles.zeinaArtImg}/>
        </div>
      </div>
    </section>
    <section style={mStyles.zeinaCalmWrap}>
      <div style={mStyles.zeinaInner}>
        <p style={mStyles.zeinaCalmP}>Zeina has never had anyone say anything racist to her. She knows what the word means; it comes up in history class. She feels sorry for the people who were so stuck in one language and one way of thinking that they didn't realise how fascinating other human beings are.</p>
        <p style={mStyles.zeinaCalmP}>Zeina belongs in so many ways. She's Welsh, and British, and European, and Lebanese. She's a passionate K-pop fan, and loves anime, and volleyball, and is just getting into reading history in Māori (that was a rugby connection originally). She thinks she'll work in some way to do with improving the environment – she loves nature, and loves seeing all the ways people are helping nature flourish.</p>
        <p style={mStyles.zeinaCalmPullout}>Languages weave us together. Every extra language connects us – deeply – to more people.</p>
        <p style={mStyles.zeinaCalmP}>Zeina lives in a world with more connections and less fear. A world where everyone is bilingual and most people speak four or five languages confidently. A world where we belong to each other in so many different ways and as part of so many different groups it's becoming impossible to imagine that we used to be lonely and afraid. In Zeina's world, there are always some new words to learn, and we enjoy getting to know each other so much.</p>
        <p style={mStyles.zeinaCalmSig}>It's the world I want my children to live in, too.</p>
      </div>
    </section>
    </>
  );
}

export function MissionWeb() {
  return (
    <section style={mStyles.webWrap} data-screen-label="Every language">
      <div style={mStyles.webInner}>
        <div style={mStyles.kicker}>Why language matters</div>
        <h2 style={mStyles.h2}>Every language is a way of being human.</h2>
        <p style={mStyles.p}>Language is uniquely responsible for how we experience being human. Even if you only speak one language, it's what shapes your brain, your capacity for thought, your understanding of the world around you – and it gives you the almost magical ability to understand other people, to hear about their experiences and ideas and emotions. It lets you become part of their life, and lets them become part of yours.</p>
        <p style={mStyles.pEmph}>Language is your interface to the web of existence. It's why and how you're not alone.</p>
        <p style={mStyles.p}>And each language is the sum total of all the people who have spoken it. The cultural code of an entire people is passed down in the words they use and the angles from which they look at life.</p>
        <p style={mStyles.p}>When a language dies, a people and a way of being are silenced, and the whole of humanity is poorer as a result. It's like a person losing a finger – you can live without it, but it was part of you. Or maybe it's more like a person losing parts of their memory; I've just witnessed my mother going through that journey. It's painful and debilitating.</p>
        <p style={mStyles.p}>Variety and diversity make us stronger – they give us more capacity for change and adaptation, which we need now more than ever.</p>
        <p style={mStyles.p}>Everybody who speaks more than one language already knows this – you become more. You're never entirely the same person in each of your languages. Each new language opens a new window on the world, and that has a real impact on how we think and behave – Welsh speakers voted firmly against Brexit, for example, perhaps because they had more information from more different sources.</p>
        <p style={mStyles.p}>And as humans, we recognise the unique value of learning languages. When I meet a tourist from America in Caernarfon who speaks Welsh, I'm immediately more interested in them. When I use my own bits of Italian or Croatian or Irish, doors are opened in ways that just don't happen if I expect everyone to speak English.</p>
      </div>
    </section>
  );
}

export function MissionStats() {
  const stats = [
    { big: '7,000', label: 'languages still spoken on Earth today.', src: 'UNESCO' },
    { big: '≈ 50%', label: 'are currently endangered.', src: 'Catalogue of Endangered Languages' },
    { big: '50–90%', label: 'expected to be extinct or moribund by 2100.', src: 'UNESCO projection' },
  ];
  return (
    <section style={mStyles.statsWrap} data-screen-label="Stats">
      <div style={mStyles.statsInner}>
        <h2 style={mStyles.statsHeadline}>
          <span style={mStyles.statsBut}>But – </span>90% of our languages are at risk.
        </h2>
        <div style={mStyles.statsGrid} className="m-stats">
          {stats.map((s, i) => (
            <div key={i} style={mStyles.statCell}>
              <div style={mStyles.statBig}>{s.big}</div>
              <div style={mStyles.statLabel}>{s.label}</div>
              <div style={mStyles.statSrc}>{s.src}</div>
            </div>
          ))}
        </div>
        <p style={mStyles.statsCoda}>This isn't accidental. Colonial powers have consistently seen language diversity as a threat to political control.</p>
      </div>
    </section>
  );
}

/* SVG token – drawn once, recoloured per item. A wooden plaque on a string. */
function NotToken({ wood, dark, letters }) {
  return (
    <svg viewBox="0 0 120 120" width="120" height="120" aria-hidden="true">
      {/* string */}
      <path d="M60 6 C 50 22, 40 30, 30 38" stroke="#3a2412" strokeWidth="1.4" fill="none"/>
      <path d="M60 6 C 70 22, 80 30, 90 38" stroke="#3a2412" strokeWidth="1.4" fill="none"/>
      {/* plaque */}
      <g transform="translate(20 38)">
        <rect width="80" height="56" rx="4" fill={wood}/>
        {/* wood grain */}
        <path d="M4 12 Q 40 16 76 12" stroke={dark} strokeWidth="0.9" fill="none" opacity=".55"/>
        <path d="M4 26 Q 40 22 76 28" stroke={dark} strokeWidth="0.9" fill="none" opacity=".45"/>
        <path d="M4 40 Q 40 44 76 40" stroke={dark} strokeWidth="0.9" fill="none" opacity=".5"/>
        {/* drilled holes for the string */}
        <circle cx="10" cy="6" r="1.6" fill="#1a0e06"/>
        <circle cx="70" cy="6" r="1.6" fill="#1a0e06"/>
        {/* burned-in letters */}
        <text x="40" y="36" textAnchor="middle"
              fontFamily="Georgia, 'Times New Roman', serif"
              fontWeight="700" fontSize="22"
              fill="rgba(0,0,0,.55)" letterSpacing=".06em">
          {letters}
        </text>
        {/* shading */}
        <rect width="80" height="56" rx="4" fill="url(#mShade)"/>
      </g>
      <defs>
        <linearGradient id="mShade" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="rgba(255,255,255,.06)"/>
          <stop offset="1" stopColor="rgba(0,0,0,.18)"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

export function MissionWelshNot() {
  const items = [
    { name: 'Welsh Not',  place: 'Wales',                  letters: 'WN', wood:'#a87447', dark:'#5d361a' },
    { name: 'le symbole', place: 'France',                 letters: 'S',  wood:'#9c6635', dark:'#523018' },
    { name: 'la vache',   place: 'Brittany',               letters: 'V',  wood:'#8c5a2c', dark:'#4a2a14' },
    { name: 'bata scóir', place: 'Ireland',                letters: 'BS', wood:'#825027', dark:'#43240f' },
    { name: 'hōgen-fuda', place: 'Okinawa & Hokkaido',     letters: '方言', wood:'#9a6234', dark:'#4d2a14' },
  ];
  return (
    <section style={mStyles.notWrap} data-screen-label="Welsh Not">
      <div style={mStyles.notInner}>
        <div style={mStyles.notProse}>
          <div style={mStyles.kicker}>Who this hurts most</div>
          <h2 style={mStyles.h2}>Again and again, children have taken the brunt of this.</h2>
          <p style={mStyles.p}>In dozens of countries, on every inhabited continent, children have been punished by their schools for speaking their own language. The same pattern – a wooden token round the neck, soap in the mouth, a beating, a humiliation – is repeated so consistently across so many places that it can't be coincidence.</p>
          <p style={mStyles.p}>In Wales we called it the Welsh Not. In France, it was le symbole. In Brittany, la vache. In Ireland, the bata scóir. In Okinawa and Hokkaido, the hōgen-fuda. The rules were the same: get caught speaking your own language at school, and you have to wear it. You can pass it on by catching another child speaking your language. Whoever is wearing it at the end of the day is punished.</p>
        </div>

        <ul style={mStyles.notRow} className="m-not-row">
          {items.map(it => (
            <li key={it.name} style={mStyles.notCard}>
              <NotToken wood={it.wood} dark={it.dark} letters={it.letters}/>
              <div style={mStyles.notName}>{it.name}</div>
              <div style={mStyles.notPlace}>{it.place}</div>
            </li>
          ))}
        </ul>

        <div style={mStyles.notProse}>
          <p style={mStyles.notCoda}>The same object, in different woods, in different empires, on different continents – worn by equally innocent children for the same damaging reason.</p>
          <p style={mStyles.notEquation}>It's the simple equation of colonial violence.</p>
          <p style={mStyles.notEquation}>Punish the children, and the community will obey.</p>
        </div>
      </div>
    </section>
  );
}

export function MissionSchools() {
  const events = [
    { year: 1914, place: 'Basque Country',         label: 'first ikastola' },
    { year: 1939, place: 'Aberystwyth, Wales',     label: 'first Welsh-medium primary' },
    { year: 1973, place: 'Ireland',                label: 'first Gaelscoil outside the Gaeltacht' },
    { year: 1977, place: 'Brittany',               label: 'Diwan opens for Breton' },
    { year: 1979, place: 'Pau, Occitania',         label: 'first Calandreta' },
    { year: 1982, place: 'Wainuiomata, Aotearoa',  label: 'first Kōhanga Reo (Māori)' },
    { year: 1983, place: 'Hawaiʻi',                label: 'Pūnana Leo opens for Hawaiian' },
  ];
  const tallies = [
    { n: '400+', label: 'Welsh-medium schools, Wales' },
    { n: '200+', label: 'Gaelscoileanna, Ireland' },
    { n: '100+', label: 'ikastolas, Basque Country' },
    { n: '20+',  label: 'Cherokee, Mohawk, Hawaiian, Greenlandic, Sámi & more' },
  ];
  return (
    <section style={mStyles.schoolsWrap} data-screen-label="Schools">
      <div style={mStyles.schoolsInner}>
        <h2 style={mStyles.h2}>But in schools today, this story is changing.</h2>
        <p style={mStyles.p}>Throughout the world, communities have built schools that teach through the medium of their own endangered languages, and where those schools have been seriously resourced, they have reliably produced confident bilingual speakers.</p>
        <p style={mStyles.p}>The first ikastola opened in the Basque Country in 1914. The first Welsh-medium primary school opened in Aberystwyth in 1939. The first Gaelscoil outside the Gaeltacht opened in Ireland in the 1970s; Diwan opened the first Breton-medium school in Brittany in 1977; the first Calandreta opened for Occitan in Pau in 1979.</p>
        <p style={mStyles.p}>Across the Pacific, the first Kōhanga Reo opened in Wainuiomata in 1982 to teach Māori children in Aotearoa (New Zealand); the Pūnana Leo opened the following year to teach Hawaiian in Hawaiʻi. Today these are not isolated experiments. There are over four hundred Welsh-medium schools in Wales, over two hundred Gaelscoileanna across Ireland, more than a hundred ikastolas in the Basque country, immersion schools for Cherokee in Oklahoma, for Mohawk in New York and Quebec, for Hawaiian in Hawaiʻi, for Greenlandic across Greenland, for Catalan in Catalonia, for Sámi in Norway at kindergarten level, and for many other languages besides.</p>

        <ol style={mStyles.tlList} className="m-tl">
          {events.map(e => (
            <li key={e.year} style={mStyles.tlItem}>
              <div style={mStyles.tlYear}>{e.year}</div>
              <div style={mStyles.tlDot} aria-hidden="true"></div>
              <div style={mStyles.tlBody}>
                <div style={mStyles.tlLabel}>{e.label}</div>
                <div style={mStyles.tlPlace}>{e.place}</div>
              </div>
            </li>
          ))}
        </ol>

        <div style={mStyles.tallyGrid} className="m-tally">
          {tallies.map((tl, i) => (
            <div key={i} style={mStyles.tallyCell}>
              <div style={mStyles.tallyN}>{tl.n}</div>
              <div style={mStyles.tallyL}>{tl.label}</div>
            </div>
          ))}
        </div>

        <p style={mStyles.pEmph}>Where this approach is properly resourced, the results are consistent.</p>
        <p style={mStyles.p}>Children leave school as confident speakers of their heritage language and of the dominant language of the country they live in. They are confident bilinguals – able to think, joke, argue, work and live in both languages.</p>
      </div>
    </section>
  );
}

export function MissionGap() {
  return (
    <section style={mStyles.gapWrap} data-screen-label="The gap">
      <div style={mStyles.gapInner}>
        <h2 style={mStyles.h2}>This answer isn't reaching most children, though.</h2>
        <p style={mStyles.p}>Minority language education works.</p>
        <p style={mStyles.p}>But most children never get it.</p>
        <p style={mStyles.p}>Most children in most countries don't go to minority-language schools. They go to schools that teach through the medium of a dominant language – English, French, Portuguese, Russian. In dominant-language schools, the heritage language is taught as a subject, maybe for two or three hours a week at the very most.</p>
        <p style={mStyles.pEmph}>You may have had that educational experience yourself.</p>
        <p style={mStyles.p}>Most people come out of it unable to hold a real conversation in the language they studied.</p>
        <p style={mStyles.p}>Generations of children have studied Welsh in English-medium schools in Wales, Irish in English-medium schools across the Republic, French in English-speaking schools in England, Spanish or French in American schools – and the overwhelming majority leave school without being conversationally confident in the language they have often been studying since the age of five.</p>
        <p style={mStyles.p}>The Welsh case is a particularly painful example: the language has every institutional advantage, millions of pounds of public investment, official status, broadcast media – and the most recent census still showed the steepest decline in young speakers between the ages of 5 and 15, because most young people in Wales attend English-medium schools and most of them never become Welsh speakers.</p>
        <ul style={mStyles.notFaultList}>
          <li style={mStyles.notFaultItem}>This isn't the fault of our teachers.</li>
          <li style={mStyles.notFaultItem}>It's not the fault of the children, either.</li>
          <li style={mStyles.notFaultItem}>It is a structural problem.</li>
        </ul>
        <p style={mStyles.p}>A couple of hours a week of language as a subject, taught with the methodologies that have been used for decades, does not produce confident speakers. It produces people who – in general – just feel bad about not being able to speak that language.</p>
        <p style={mStyles.pEmph}>And they deserve better.</p>
        <div style={mStyles.missionBlock}>
          <h2 style={mStyles.h2}>So that's our mission.</h2>
          <p style={mStyles.p}>We're focusing on how to help children become confident conversational speakers when they only have an hour or two each week.</p>
          <p style={mStyles.p}>We believe that if we can make a valuable contribution in this particular area, it could be part of a tipping point where it becomes normal for children to leave school with three or four confident conversational languages.</p>
          <p style={mStyles.p}>And we believe that would help build a better, more connected, more human world. The world that Zeina lives in, and the world I wish for my children.</p>
        </div>
      </div>
    </section>
  );
}

export function MissionTrial() {
  // Two encouraging signs: (1) passive-to-active activation, (2) Cardiff reverse bell.
  // Reverse bell vs bottom-loaded distribution. 10 deciles, bottom 10% to top 10%.
  const ssi  = [42, 8, 4, 3, 2, 2, 2, 4, 8, 42];
  const ctrl = [40, 14, 6, 3, 2, 1, 1, 0, 0, 0];
  const max  = Math.max(...ssi, ...ctrl);
  const W = 760, H = 320, padX = 56, padTop = 30, padBot = 56;
  const slot = (W - padX*2) / 10;
  const bw   = slot * 0.42;
  const drawBars = (vals, fill, offset) => vals.map((v, i) => {
    const h = (v / max) * (H - padTop - padBot);
    const x = padX + i*slot + offset;
    const y = H - padBot - h;
    return <rect key={i} x={x} y={y} width={bw} height={h} rx="2" fill={fill}/>;
  });
  return (
    <section style={mStyles.trialWrap} data-screen-label="Encouraging signs">
      <div style={mStyles.trialInner}>
        <div style={mStyles.kicker}>Encouraging signs</div>
        <h2 style={mStyles.h2}>We've seen two interesting things that give us hope.</h2>

        <div style={mStyles.signBlock}>
          <div style={mStyles.signLabel}>One</div>
          <p style={mStyles.p}>When people start learning with us – in other words, doing a lot of intense speaking practice – it often seems to activate language that they had previously learnt passively.</p>
          <p style={mStyles.p}>When people who did Welsh as a subject in English-medium schools start learning with us, the results can be quite startling – to the point where we now believe that language education in dominant-language schools isn't actually failing. We think it's succeeding much more than is recognised, but it's missing the core ingredient of spoken practice.</p>
          <p style={mStyles.pEmph}>That's where we think we can make a contribution.</p>
        </div>

        <div style={mStyles.signBlock}>
          <div style={mStyles.signLabel}>Two</div>
          <p style={mStyles.p}>We ran a trial in a cluster of primary schools in Cardiff. The results were assessed independently by a Cardiff University PhD student, and they showed unexpected results. Classes using the national-curriculum and the local consortium approach did poorly in the assessment, with most students scoring in the bottom 10% on a bar chart. Classes that had used the SaySomethingin Method – even for as little as five minutes a week – saw an interesting reverse bell curve.</p>

        <figure style={mStyles.chartFig}>
          <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Illustrative sketch: SSi-method classes clustered at both ends, while comparison classes were bottom-loaded. Shape only; not the underlying data.">
            <defs>
              <filter id="sketchSoft" x="-5%" y="-5%" width="110%" height="110%">
                <feGaussianBlur stdDeviation="0.6"/>
              </filter>
            </defs>
            {/* baseline – soft, hand-drawn feel */}
            <path
              d={`M ${padX} ${H-padBot} Q ${W/2} ${H-padBot+2}, ${W-padX} ${H-padBot}`}
              stroke="rgba(15,18,18,.18)" strokeWidth="1.2" fill="none"
              strokeLinecap="round"/>
            {/* control: bottom-loaded, single soft mound at the left */}
            {(() => {
              const xs = ctrl.map((_, i) => padX + (i + 0.5) * slot);
              const ys = ctrl.map(v => H - padBot - (v / max) * (H - padTop - padBot));
              const pts = xs.map((x, i) => `${x},${ys[i]}`).join(' L ');
              const area = `M ${padX} ${H-padBot} L ${pts} L ${W-padX} ${H-padBot} Z`;
              return (
                <g filter="url(#sketchSoft)">
                  <path d={area} fill="rgba(189,182,179,.55)"/>
                  <path d={`M ${pts}`.replace(' L ', ' M ').replace(/^M /, 'M ').replace('M', 'M').split(' ').slice(0,1).join(' ') + ' L ' + pts.split(' L ').slice(1).join(' L ')}
                        stroke="#8a8380" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
              );
            })()}
            {/* SSi: two clusters, low middle – the reverse bell */}
            {(() => {
              const xs = ssi.map((_, i) => padX + (i + 0.5) * slot);
              const ys = ssi.map(v => H - padBot - (v / max) * (H - padTop - padBot));
              const pts = xs.map((x, i) => `${x},${ys[i]}`).join(' L ');
              const area = `M ${padX} ${H-padBot} L ${pts} L ${W-padX} ${H-padBot} Z`;
              return (
                <g filter="url(#sketchSoft)">
                  <path d={area} fill="rgba(219,30,23,.32)"/>
                  <path d={'M ' + pts} stroke="#DB1E17" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
              );
            })()}
          </svg>
          <figcaption style={mStyles.chartCap}>Illustrative sketch – not the exact data.</figcaption>
        </figure>

        <p style={mStyles.pEmph}>Half the students scored in the bottom 10%. The other half scored in the top 10%.</p>
        <p style={mStyles.p}>In other words, engaged students took a huge leap forwards even in five minutes a week. In many classes, the successful students included those who weren't seen as academically strong – which calls for further study as a tool for learning inclusion.</p>
        <p style={mStyles.p}>We think that five minutes a day of additional speaking exercises would give children a new confidence in their own ability, and help them reach at least B2 (on the Common European Framework for Languages) by the time they leave school.</p>
        <p style={mStyles.pSmall}>Read the full study: <a style={mStyles.inlineA} href="https://orca.cardiff.ac.uk/id/eprint/174145/" target="_blank" rel="noopener">orca.cardiff.ac.uk/id/eprint/174145</a></p>
        </div>
      </div>
    </section>
  );
}

export function MissionDoing() {
  return (
    <section style={mStyles.doingWrap} data-screen-label="What we're doing">
      <div style={mStyles.doingInner}>
        <div style={mStyles.kicker}>What we're trying to do</div>
        <h2 style={mStyles.h2}>Help children become confident conversational speakers – in less than an hour each week.</h2>
        <p style={mStyles.p}>I wrote the first version of the SaySomethingin Method – our Welsh course – because becoming a Welsh speaker in my early 30s was such an emotional, life-changing experience for me. I wanted other people to be able to come home in the same way. We've been supported and inspired by our community of Welsh learners ever since.</p>
        <p style={mStyles.p}>Now, we're building a schools interface. We're going to make minority languages available for free at the point of use for schools and for individual learners (we're going to have to figure out how to make sure we cover the costs – they're very small per user, but they still add up).</p>
        <p style={mStyles.p}>We're aiming to sell courses for 10 major languages – Spanish, French, English, German, Italian, Portuguese, Mandarin, Japanese, Korean, Arabic – in order to fund building courses for all other languages.</p>
        <h3 style={mStyles.h3}>The Popty <span style={mStyles.h3Aside}>(that's Welsh for oven)</span></h3>
        <p style={mStyles.p}>Our Popty is a course creation tool that (with a little help) can build courses that follow the SaySomethingin Method for any language. There's some complicated stuff under the hood, but we're always simplifying the user interface – we've built 25 free courses with it, and we're starting to let other people test drive building their own courses with it now.</p>
        <p style={mStyles.p}>That's how we hope to be able to build a course for all 7,000 languages.</p>
        <p style={mStyles.p}>We're planning to fund the course builder and the free courses with the income from the 10 paid-for languages. In other words, we're trying to create a feedback loop where the economic value of the dominant languages helps undo the negative impact they've had on minority languages.</p>

        <div style={mStyles.loopGrid} className="m-loop">
          <div style={mStyles.loopCell}>
            <div style={mStyles.loopN}>10</div>
            <div style={mStyles.loopL}>paid courses to fund the work</div>
            <div style={mStyles.loopP}>Spanish, French, English, German, Italian, Portuguese, Mandarin, Japanese, Korean, Arabic.</div>
          </div>
          <div style={mStyles.loopArrow} aria-hidden="true">→</div>
          <div style={mStyles.loopCell}>
            <div style={mStyles.loopN}>25</div>
            <div style={mStyles.loopL}>free courses already built</div>
            <div style={mStyles.loopP}>Working towards a free SSi course for every minority and endangered language.</div>
          </div>
          <div style={mStyles.loopArrow} aria-hidden="true">→</div>
          <div style={mStyles.loopCell}>
            <div style={mStyles.loopN}>7,000</div>
            <div style={mStyles.loopL}>the goal</div>
            <div style={mStyles.loopP}>An accessible course builder, so any community can build a course in any language.</div>
          </div>
        </div>

      </div>
    </section>
  );
}

export function MissionContact() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (e.target.elements.website && e.target.elements.website.value) return;
    setSubmitting(true);
    setError(null);
    const data = Object.fromEntries(new FormData(e.target).entries());
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form: 'mission', ...data }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
    } catch {
      setError('Something went wrong — please try again or email us at hello@saysomethingin.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section style={mStyles.formWrap} data-screen-label="Contact">
      <div style={mStyles.formInner}>
        <div style={mStyles.kicker}>Get in touch</div>
        <h2 style={mStyles.h2}>If you like this idea, and you'd like to help – we'd love to hear from you.</h2>
        <p style={mStyles.p}>I mean, we'd <em>really</em> love to hear from you. You might have noticed that this isn't a small challenge.</p>
        <p style={mStyles.p}>We're a small company – less than a dozen people, based in rural Gwynedd, trying to do something really difficult. We're not going to stop trying – we care too much about it – but there's no denying that we're much more likely to succeed if we work in partnership with other people who care as much as we do about languages and bringing people together.</p>
        <p style={mStyles.p}>Here's a little form to help you get in touch. Or you can just email us at <a style={mStyles.inlineA} href="mailto:hello@saysomethingin.com">hello@saysomethingin.com</a>.</p>

        {sent ? (
          <div style={mStyles.thankYou}>
            <div style={mStyles.thankH}>Diolch o galon.</div>
            <p style={mStyles.p}>We'll be in touch. Thank you for caring about this.</p>
          </div>
        ) : (
          <form style={mStyles.form} onSubmit={onSubmit}>
            <div aria-hidden="true" style={{position:'absolute', left:'-10000px', top:'auto', width:1, height:1, overflow:'hidden'}}>
              <label>Website (leave this empty)<input type="text" name="website" tabIndex={-1} autoComplete="off"/></label>
            </div>
            <label style={mStyles.field}>
              <span style={mStyles.fieldL}>Your name</span>
              <input type="text" name="name" required style={mStyles.input}/>
            </label>
            <label style={mStyles.field}>
              <span style={mStyles.fieldL}>Email</span>
              <input type="email" name="email" required style={mStyles.input}/>
            </label>
            <label style={{...mStyles.field, gridColumn:'1 / -1'}}>
              <span style={mStyles.fieldL}>How would you like to help?</span>
              <select name="topic" required style={mStyles.input} defaultValue="">
                <option value="" disabled>Choose one…</option>
                <option>I want to support a specific language</option>
                <option>I work in education and want to talk</option>
                <option>I'd like to partner / collaborate</option>
                <option>I just want to learn more</option>
                <option>Something else</option>
              </select>
            </label>
            <label style={{...mStyles.field, gridColumn:'1 / -1'}}>
              <span style={mStyles.fieldL}>Tell us a little</span>
              <textarea name="message" rows="5" style={{...mStyles.input, resize:'vertical', minHeight:120}}/>
            </label>
            {error && <p style={{...mStyles.p, gridColumn:'1 / -1', color:'var(--brandRed)', margin:0}}>{error}</p>}
            <div style={{gridColumn:'1 / -1', display:'flex', gap:12, flexWrap:'wrap'}}>
              <button className="btn btn--primary" type="submit" disabled={submitting}>{submitting ? 'Sending…' : 'Send'}</button>
              <Link to="/about" className="btn btn--ghost" style={{border:'1px solid var(--border-strong)'}}>Meet the team</Link>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

const mStyles = {
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(34px, 4vw, 48px)', lineHeight:1.06, letterSpacing:'-.012em', margin:'0 0 24px', textWrap:'balance' },
  h3: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(24px, 2.6vw, 32px)', lineHeight:1.15, letterSpacing:'-.008em', margin:'40px 0 18px', textWrap:'balance' },
  h3Aside: { fontStyle:'italic', fontWeight:400, color:'var(--grey)', fontSize:'.72em' },
  p: { fontSize:18, lineHeight:1.65, color:'var(--fg-1)', margin:'0 0 18px', maxWidth:'62ch', textWrap:'pretty' },
  pSmall: { fontSize:14, color:'var(--grey)', margin:'0 0 16px' },
  notEquation: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(22px, 2.4vw, 28px)', lineHeight:1.4, color:'var(--brandRed)', margin:'12px 0 24px', maxWidth:'48ch', textWrap:'balance' },
  pEmph: { fontFamily:'var(--font-display)', fontStyle:'italic', fontSize:'clamp(22px, 2.4vw, 28px)', lineHeight:1.4, color:'var(--brandRed)', margin:'12px 0 24px', maxWidth:'34ch', textWrap:'balance' },
  inlineA: { color:'var(--brandRed)', fontWeight:600 },

  /* hero */
  heroWrap: { background:'var(--offWhite)', padding:'120px 32px 96px', borderBottom:'1px solid var(--border)' },
  heroInner: { maxWidth:880, margin:'0 auto' },
  heroKicker: { fontSize:13, letterSpacing:'.18em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:22 },
  heroH1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(48px, 7vw, 88px)', lineHeight:.98, letterSpacing:'-.025em', margin:'0 0 36px', textWrap:'balance' },
  royFig: { margin:'8px 0 40px', padding:'20px 28px 20px 32px', borderLeft:'3px solid var(--brandRed)', background:'linear-gradient(90deg, rgba(219,30,23,0.10) 0%, rgba(219,30,23,0.04) 45%, rgba(219,30,23,0) 90%)', borderRadius:'0 8px 8px 0' },
  royQuote: { fontFamily:'var(--font-display)', fontStyle:'italic', fontSize:'clamp(24px, 3vw, 32px)', lineHeight:1.3, color:'var(--fg-1)', margin:0, textWrap:'balance' },
  heroP: { fontSize:18, lineHeight:1.65, color:'var(--fg-1)', margin:'0 0 18px', maxWidth:'62ch' },
  heroPFinal: { fontFamily:'var(--font-display)', fontSize:'clamp(22px, 2.4vw, 28px)', lineHeight:1.4, color:'var(--fg-1)', maxWidth:'40ch' },

  /* zeina */
  zeinaWrap: { background:'var(--brandRed)', color:'#fff', padding:'72px 32px 96px' },
  zeinaGrid: { display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 320px)', gap:48, alignItems:'center' },
  zeinaArtWrap: { width:'100%', maxWidth:320, justifySelf:'end' },
  zeinaArtImg: { width:'100%', height:'auto', display:'block' },
  zeinaCalmWrap: { background:'var(--offWhite)', color:'var(--fg-1)', padding:'96px 32px 96px' },
  zeinaCalmP: { fontSize:19, lineHeight:1.65, color:'var(--fg-1)', margin:'0 0 18px', maxWidth:'58ch', textWrap:'pretty' },
  zeinaCalmPullout: { fontFamily:'var(--font-display)', fontStyle:'italic', fontSize:'clamp(28px, 3.4vw, 40px)', lineHeight:1.25, color:'var(--brandRed)', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', padding:'28px 0', margin:'32px 0', textWrap:'balance', maxWidth:'24ch' },
  zeinaCalmSig: { fontFamily:'var(--font-display)', fontSize:'clamp(28px, 3.4vw, 40px)', lineHeight:1.25, color:'var(--grey)', marginTop:32, textWrap:'balance', maxWidth:'24ch' },
  zeinaInner: { maxWidth:880, margin:'0 auto' },
  zeinaKicker: { fontFamily:'var(--font-display)', fontWeight:700, fontStyle:'italic', fontSize:'clamp(40px, 6vw, 64px)', color:'var(--gold)', margin:'0 0 24px', letterSpacing:'-.01em' },
  zeinaLead: { fontFamily:'var(--font-display)', fontSize:'clamp(24px, 2.4vw, 30px)', lineHeight:1.35, color:'#fff', margin:'0 0 22px', textWrap:'pretty', maxWidth:'48ch' },
  zeinaP: { fontSize:19, lineHeight:1.65, color:'rgba(255,255,255,.92)', margin:'0 0 18px', maxWidth:'58ch', textWrap:'pretty' },
  zeinaPullout: { fontFamily:'var(--font-display)', fontStyle:'italic', fontSize:'clamp(28px, 3.4vw, 40px)', lineHeight:1.25, color:'var(--gold)', borderTop:'1px solid rgba(255,255,255,.2)', borderBottom:'1px solid rgba(255,255,255,.2)', padding:'28px 0', margin:'32px 0', textWrap:'balance', maxWidth:'24ch' },
  zeinaSig: { fontStyle:'italic', color:'rgba(255,255,255,.7)', fontSize:15, marginTop:20 },

  /* web */
  webWrap: { background:'#fff', padding:'120px 32px 96px' },
  webInner: { maxWidth:760, margin:'0 auto' },

  /* stats */
  statsWrap: { background:'var(--brandRed)', color:'#fff', padding:'104px 32px 104px' },
  statsInner: { maxWidth:1080, margin:'0 auto' },
  statsHeadline: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(44px, 6vw, 76px)', lineHeight:1.02, letterSpacing:'-.022em', color:'#fff', margin:'0 0 24px', textWrap:'balance', maxWidth:'20ch' },
  statsBut: { color:'rgba(255,255,255,.55)' },
  statsGrid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:32, margin:'56px 0 40px' },
  statCell: { borderTop:'2px solid rgba(255,255,255,.4)', paddingTop:18 },
  statBig: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:'clamp(56px, 7vw, 96px)', lineHeight:.95, letterSpacing:'-.025em', marginBottom:12 },
  statLabel: { fontSize:17, lineHeight:1.45, color:'rgba(255,255,255,.95)', maxWidth:'32ch', marginBottom:10 },
  statSrc: { fontSize:12, letterSpacing:'.1em', textTransform:'uppercase', color:'rgba(255,255,255,.7)' },
  statsCoda: { fontFamily:'var(--font-display)', fontSize:'clamp(20px, 2vw, 26px)', lineHeight:1.45, color:'rgba(255,255,255,.95)', maxWidth:'52ch', marginTop:12, textWrap:'balance' },

  /* welsh not */
  notWrap: { background:'var(--offWhite)', padding:'120px 32px 96px' },
  notInner: { maxWidth:1080, margin:'0 auto' },
  notProse: { maxWidth:760, margin:'0 auto' },
  notRow: { listStyle:'none', padding:0, margin:'48px 0 32px', display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:24, justifyItems:'center' },
  notCard: { display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', gap:10 },
  notName: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:18, fontStyle:'italic', color:'var(--fg-1)', marginTop:4 },
  notPlace: { fontSize:13, color:'var(--grey)' },
  notCoda: { fontSize:18, lineHeight:1.6, color:'var(--fg-1)', maxWidth:'62ch', margin:'12px 0 4px' },

  /* schools */
  schoolsWrap: { background:'#fff', padding:'120px 32px 96px' },
  schoolsInner: { maxWidth:760, margin:'0 auto' },
  tlList: { listStyle:'none', padding:0, margin:'40px 0 48px', display:'grid', gridTemplateColumns:'120px 24px 1fr' },
  tlItem: { display:'contents' },
  tlYear: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:24, color:'var(--brandRed)', padding:'18px 0', borderTop:'1px solid var(--border)' },
  tlDot: { borderTop:'1px solid var(--border)' },
  tlBody: { padding:'18px 0', borderTop:'1px solid var(--border)' },
  tlLabel: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:20, color:'var(--fg-1)' },
  tlPlace: { fontSize:14, color:'var(--grey)', marginTop:4 },
  tallyGrid: { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:20, margin:'8px 0 36px', padding:'32px', background:'var(--bg-soft)', borderRadius:16 },
  tallyCell: {},
  tallyN: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:36, color:'var(--brandRed)', lineHeight:1, marginBottom:8, letterSpacing:'-.02em' },
  tallyL: { fontSize:14, color:'var(--fg-1)', lineHeight:1.4 },

  /* gap */
  gapWrap: { background:'var(--offWhite)', padding:'120px 32px 96px' },
  gapInner: { maxWidth:760, margin:'0 auto' },
  notFaultList: { listStyle:'none', padding:0, margin:'24px 0 28px', display:'flex', flexDirection:'column', gap:12 },
  notFaultItem: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:22, color:'var(--fg-1)', borderLeft:'3px solid var(--brandRed)', paddingLeft:18, lineHeight:1.4 },
  missionBlock: { marginTop:48, paddingTop:40, borderTop:'2px solid var(--brandRed)' },

  /* trial signs */
  signBlock: { margin:'40px 0 32px' },
  signLabel: { fontFamily:'var(--font-display)', fontWeight:700, fontStyle:'italic', fontSize:20, color:'var(--brandRed)', marginBottom:14, letterSpacing:'.02em' },

  /* trial chart */
  trialWrap: { background:'#fff', padding:'120px 32px 96px' },
  trialInner: { maxWidth:880, margin:'0 auto' },
  chartFig: { margin:'40px 0 24px', padding:0, background:'transparent' },
  chartCap: { fontStyle:'italic', fontSize:13, color:'var(--grey)', marginTop:8, textAlign:'left' },
  chartKey: { display:'inline-flex', alignItems:'center', gap:8 },
  swatch: { display:'inline-block', width:14, height:14, borderRadius:3 },

  /* doing */
  doingWrap: { background:'var(--bg-soft)', padding:'120px 32px 96px' },
  doingInner: { maxWidth:1080, margin:'0 auto' },
  loopGrid: { display:'grid', gridTemplateColumns:'1fr auto 1fr auto 1fr', gap:20, alignItems:'stretch', margin:'40px 0 32px' },
  loopCell: { background:'#fff', borderRadius:16, padding:'24px 22px', border:'1px solid var(--border)' },
  loopArrow: { display:'flex', alignItems:'center', justifyContent:'center', color:'var(--brandRed)', fontSize:28, fontWeight:700 },
  loopN: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:48, color:'var(--brandRed)', lineHeight:1, letterSpacing:'-.02em', marginBottom:6 },
  loopL: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:18, color:'var(--fg-1)', marginBottom:8 },
  loopP: { fontSize:14, color:'var(--grey)', lineHeight:1.5, margin:0 },

  /* form */
  formWrap: { background:'#fff', padding:'120px 32px 120px' },
  formInner: { maxWidth:760, margin:'0 auto' },
  form: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:18, marginTop:24 },
  field: { display:'flex', flexDirection:'column', gap:6 },
  fieldL: { fontSize:11, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--grey)', fontWeight:600 },
  input: { padding:'12px 14px', borderRadius:10, border:'1px solid var(--border-strong)', fontFamily:'inherit', fontSize:16, color:'var(--fg-1)', background:'#fff' },
  thankYou: { padding:32, background:'var(--bg-soft)', borderRadius:16, marginTop:16 },
  thankH: { fontFamily:'var(--font-display)', fontStyle:'italic', fontSize:32, color:'var(--brandRed)', marginBottom:8 },
};
